import { action, atom } from '@reatom/framework'
import * as DateF from 'date-fns'
import i18next from 'i18next'

import { Data, Result } from '@blade/std-lib'
import { TrackingWallet } from '@root/entities/wallet-tracking'
import { PlatformUtil } from '@root/shared'
import { reatomRequest } from '@root/shared/lib'
import { showErrorSnackbarAction } from '@root/shared/ui'
import { showSnackbarAction } from '@root/shared/ui-kit'

import { readImportedWallets } from '../model/import-wallets'

import { walletTrackingAddedEvent } from './reatom-add-wallet-tracker'
import { trackingWalletsAtom } from './reatom-tracking-wallets'
import { importWalletTrackerRequestAction } from './wallet-tracker-requests'

import type { Error } from '@root/shared'

export type PortDrawerTab = 'import' | 'export'

export const reatomPortWalletsTabAtom = atom<PortDrawerTab>(
  'export',
  'reatomPortWalletsTabAtom',
)

export class ImportTrackingWalletsError extends Data.TaggedError(
  'InvalidFormatError',
) {}

/**
 * What to tell the user afterwards. `skipped` is the entries the file carried
 * that this app could not use — a wallet on a chain it does not serve, a line
 * with no address at all. They are worth reporting: a file of 179 wallets that
 * imports 175 has four the user may want to look at, and saying only "175
 * imported" hides that.
 */
type ImportReport = {
  imported: number
  skipped: number
}

const processImportWalletsAction = action(
  async (
    ctx,
    plainText: string,
  ): Promise<
    Result.Result<ImportReport, ImportTrackingWalletsError | Error.UnknownError>
  > => {
    const parsedJson = Result.try({
      try: () => JSON.parse(plainText) as unknown,
      catch: () => new ImportTrackingWalletsError(),
    })

    if (Result.isFailure(parsedJson)) {
      return Result.fail(parsedJson.failure)
    }

    // Whichever terminal wrote the file. An entry that cannot be read is
    // stepped over; only a file that is not a list of wallets at all is
    // refused, because that is the one case the reader cannot make sense of.
    const outcome = readImportedWallets(parsedJson.success)
    if (!outcome || outcome.wallets.length === 0) {
      return Result.fail(new ImportTrackingWalletsError())
    }

    const imported = await ctx.schedule(() =>
      importWalletTrackerRequestAction(ctx, outcome.wallets),
    )
    if (Result.isFailure(imported)) {
      return Result.fail(imported.failure)
    }

    return Result.succeed({
      imported: imported.success.length,
      skipped: outcome.skipped,
    })
  },
  'processImportWalletsAction',
)

export const processExportWalletsAction = action(async (ctx) => {
  const content = ctx.get(exportedWalletsContentAtom)
  if (!content) {
    return
  }
  const blob = new Blob([content], { type: 'text/plain' })
  const fileName = `kol-trade-tracked-wallets-${DateF.format(new Date(), 'yyyy-MM-dd-HH-mm-ss')}.txt`
  const result = await ctx.schedule(() =>
    PlatformUtil.downloadFile(blob, fileName),
  )

  if (Result.isFailure(result)) {
    showSnackbarAction(ctx, {
      intent: 'error',
      text: i18next.t('wallet-tracker.port.export-error'),
    })
    return
  }
  showSnackbarAction(ctx, {
    intent: 'success',
    text: i18next.t('wallet-tracker.port.export-success'),
  })
}, 'processExportWalletsAction')

export const reatomImportWallets = () => {
  // eslint-disable-next-line @reatom/unit-naming-rule
  const requestState = reatomRequest({
    execAction: processImportWalletsAction,
    onResult: (ctx, result) => {
      if (Result.isFailure(result)) {
        if (result.failure._tag === 'InvalidFormatError') {
          showSnackbarAction(ctx, {
            text: i18next.t('wallet-tracker.port.import.wrong-format'),
            intent: 'error',
          })
          return
        }
        showErrorSnackbarAction(ctx, result.failure)
        return
      }

      const { imported, skipped } = result.success

      showSnackbarAction(ctx, {
        text: skipped
          ? i18next.t('wallet-tracker.port.import-partial-success', {
              count: imported,
              skipped,
            })
          : i18next.t('wallet-tracker.port.import-success', {
              count: imported,
            }),
        intent: 'success',
      })
      walletTrackingAddedEvent(ctx)
    },
  })

  return requestState
}

export const exportedWalletsContentAtom = atom((ctx) => {
  const wallets = ctx.spy(trackingWalletsAtom)
  if (wallets.length === 0) {
    return undefined
  }
  const dtos = wallets.map(TrackingWallet.toDto)
  return JSON.stringify(dtos, undefined, 4)
}, 'exportedWalletsContentAtom')
